import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

import { createApInvoiceCbpTsh, fetchApInvoiceCbpTsh } from "./apInvoicesApi";

function formatPmsDueLabel(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (match == null) {
    return isoDate;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

export function ApInvoiceCbpTshPanel(props: { invoiceId: string }): JSX.Element | null {
  const queryClient = useQueryClient();
  const splitQuery = useQuery({
    queryKey: ["accounting", "ap-invoice-cbp-tsh", props.invoiceId],
    queryFn: () => fetchApInvoiceCbpTsh(props.invoiceId),
  });
  const [amount, setAmount] = useState("");
  const [entryDate, setEntryDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createApInvoiceCbpTsh(props.invoiceId, {
        amount: Number.parseFloat(amount),
        entry_date: entryDate,
      }),
    onSuccess: (split) => {
      setError(null);
      queryClient.setQueryData(["accounting", "ap-invoice-cbp-tsh", props.invoiceId], split);
      void queryClient.invalidateQueries({ queryKey: ["accounting", "ap-invoices"] });
    },
    onError: (createError: unknown) => {
      setError(createError instanceof Error ? createError.message : "Could not create the CBP TSH.");
    },
  });

  const split = splitQuery.data;
  if (splitQuery.isLoading) {
    return <p className="text-sm text-slate-500">Checking for a CBP notice…</p>;
  }
  if (split?.role === "cbp" && split.source_accounts_payable_invoice_id != null) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
        <p className="font-medium">Periodic Monthly Statement</p>
        <p className="mt-1">
          CBP TSH from shipper invoice{" "}
          <Link
            className="font-medium text-brand-700 hover:underline"
            to={`/accounting/ap/${split.source_accounts_payable_invoice_id}/edit`}
          >
            {split.source_vendor_invoice_number?.trim() || split.source_accounts_payable_invoice_id}
          </Link>
          . Customs debits this on the 15th business day of the month after the entry date
          {split.pms_due_on != null ? `, so funds are due ${formatPmsDueLabel(split.pms_due_on)}` : ""}. Have the
          money in the account before that date. This does not wait for the Megaventory TSH.{" "}
          <Link className="font-medium text-brand-700 hover:underline" to="/accounting/cbp-monthly-statements">
            Monthly statements
          </Link>
        </p>
      </div>
    );
  }
  if (split?.role === "source" && split.created_accounts_payable_invoice_id != null) {
    return (
      <p className="text-sm text-slate-700">
        CBP TSH created for {split.amount}{" "}
        {split.supplier_name != null ? `(${split.supplier_name})` : ""}.
        {split.pms_due_on != null
          ? ` Periodic Monthly Statement funds are due ${formatPmsDueLabel(split.pms_due_on)}.`
          : ""}{" "}
        <Link
          className="font-medium text-brand-700 hover:underline"
          to={`/accounting/ap/${split.created_accounts_payable_invoice_id}/edit`}
        >
          Open CBP TSH
        </Link>
        .{" "}
        <Link className="font-medium text-brand-700 hover:underline" to="/accounting/cbp-monthly-statements">
          Monthly statements
        </Link>
      </p>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <p className="text-sm font-medium text-slate-900">CBP notice</p>
      <p className="mt-1 text-sm text-slate-600">
        Use this when the shipper invoice also says an amount is owed to CBP. This invoice stays the shipper TSH.
        The CBP amount becomes a second TSH on the same GIPI.
      </p>
      <p className="mt-2 text-sm text-slate-700">Vendor: Customs and Border Patrol (CBP)</p>
      <div className="mt-3 grid max-w-xl gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="cbp-tsh-entry-date">
            Entry date
          </label>
          <Input
            id="cbp-tsh-entry-date"
            type="date"
            value={entryDate}
            disabled={createMutation.isPending}
            onChange={(event) => setEntryDate(event.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            The entry date on the CBP entry summary, not the shipper invoice date.
          </p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="cbp-tsh-amount">
            Amount owed to CBP
          </label>
          <Input
            id="cbp-tsh-amount"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            disabled={createMutation.isPending}
            onChange={(event) => setAmount(event.target.value)}
          />
        </div>
      </div>
      {error != null ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
      <Button
        type="button"
        className="mt-3"
        variant="secondary"
        disabled={createMutation.isPending || amount.trim() === "" || entryDate.trim() === ""}
        onClick={() => createMutation.mutate()}
      >
        {createMutation.isPending ? "Creating…" : "Create CBP TSH"}
      </Button>
    </div>
  );
}
